import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Download, Phone } from "lucide-react";

import { PageHero } from "@/components/page-parts";
import { FIRM, FIRM_ADDRESS, localBusinessSchema } from "@/lib/firm";
import { GUIDES, type Guide } from "@/lib/guides";

const CORE_SLUGS = [
  "granny-flat-arrangements-queensland",
  "granny-flat-options-compared-ownership-lease-licence",
] as const;

const ELDER_SLUGS = [
  "buying-into-a-retirement-village-in-queensland",
  "enduring-power-of-attorney-qld",
  "attorney-duties-enduring-power-of-attorney-qld",
  "advance-health-directive-queensland",
] as const;

const ESTATE_SLUGS = [
  "when-to-update-your-will-queensland",
  "contesting-a-will-in-queensland",
  "estate-contested-executor-guide-queensland",
  "what-happens-if-you-die-without-a-will-in-queensland",
] as const;

const PROPERTY_SLUGS = [
  "how-much-does-conveyancing-cost-in-queensland",
  "cooling-off-period-queensland",
  "building-and-pest-report-came-back-bad",
] as const;

function pick(slugs: ReadonlyArray<string>): Guide[] {
  return slugs
    .map((slug) => GUIDES.find((g) => g.slug === slug))
    .filter((g): g is Guide => Boolean(g));
}

const DOWNLOADS = [
  {
    href: "/granny-flat-arrangement-questions-checklist.pdf",
    label: "Granny flat arrangement questions checklist (PDF)",
    note: "The questions every family should answer in writing before money changes hands.",
  },
  {
    href: "/contested-estate-intake-checklist.pdf",
    label: "Contested estate intake checklist (PDF)",
    note: "What we need if a granny flat arrangement ends up in an estate dispute.",
  },
] as const;

const FAQS = [
  {
    q: "What is a granny flat arrangement?",
    a: "It is a family agreement where an older person contributes money — often the proceeds of selling their home — in exchange for a right to live in a property owned by someone else, usually an adult child. It does not have to involve a physical granny flat, and the legal structure can range from co-ownership to a lease, a life interest or a simple licence.",
  },
  {
    q: "Which structure is safest?",
    a: "It depends on the money involved, the family's circumstances and how each side would recover their position if things change. Co-ownership and registered leases offer the strongest protection for the older person; licences and informal promises offer the least. The comparison guide on this page sets out the trade-offs.",
  },
  {
    q: "Does a granny flat arrangement need to be in writing?",
    a: "Legally many are enforceable without a written document, but proving them is the problem — especially after a death, separation or bankruptcy. Centrelink also expects to see evidence. We always recommend a written agreement signed by everyone contributing or receiving money.",
  },
  {
    q: "Do we need to update our wills as well?",
    a: "Almost always. A granny flat arrangement changes who holds what, and a will drafted before the arrangement usually contradicts it. We deal with the agreement and the estate planning together so the two documents say the same thing.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const collectionSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Granny flat resources (Queensland)",
  description:
    "Guides, checklists and services covering granny flat arrangements in Queensland — structures, pension and duty, estate planning and elder law.",
  isPartOf: { "@type": "WebSite", name: FIRM.name },
  hasPart: pick([...CORE_SLUGS, ...ELDER_SLUGS, ...ESTATE_SLUGS]).map((g) => ({
    "@type": "WebPage",
    headline: g.title,
    url: `https://coastsidelaw.com.au/guides/${g.slug}`,
  })),
};

export const Route = createFileRoute("/granny-flat-resources")({
  head: () => ({
    meta: [
      { title: fitTitle(`Granny Flat Resources for Queensland Families | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Every Coastside Law resource on granny flat arrangements in Queensland — structure options, checklists, pension and duty, wills, powers of attorney and estate disputes."),
      },
      { property: "og:title", content: fitTitle(`Granny flat resources | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Guides, downloadable checklists and services covering granny flat arrangements, elder law and estate planning on the Sunshine Coast."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/granny-flat-resources" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/granny-flat-resources" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localBusinessSchema) },
      { type: "application/ld+json", children: JSON.stringify(collectionSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema) },
    ],
  }),
  component: GrannyFlatResourcesPage,
});

function GuideList({
  heading,
  intro,
  guides,
}: {
  heading: string;
  intro: string;
  guides: Guide[];
}) {
  if (guides.length === 0) return null;

  return (
    <div>
      <h2 className="font-serif text-2xl text-foreground">{heading}</h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">{intro}</p>
      <ul className="mt-6 grid gap-4 sm:grid-cols-2">
        {guides.map((guide) => (
          <li key={guide.slug}>
            <Link
              to="/guides/$slug"
              params={{ slug: guide.slug }}
              className="group flex h-full flex-col rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                {guide.category}
              </span>
              <span className="mt-2 font-serif text-lg leading-snug text-foreground group-hover:text-primary">
                {guide.title}
              </span>
              <span className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {guide.description}
              </span>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                Read the guide
                <ArrowRight className="size-4" aria-hidden="true" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function GrannyFlatResourcesPage() {
  return (
    <>
      <PageHero
        eyebrow="Resource hub"
        title="Granny flat resources"
        lead="Everything we have written for Queensland families thinking about moving mum or dad in — the structure options, the money, the pension, and the wills and powers of attorney that have to line up with the arrangement."
      />

      <section className="mx-auto max-w-6xl space-y-16 px-5 py-16">
        <div className="rounded-sm border border-border bg-secondary/40 p-6 sm:p-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-accent">Start here</p>
          <h2 className="mt-3 font-serif text-2xl text-foreground">
            Talk it through before any money moves
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Most granny flat problems we see could have been prevented by a two-page agreement
            written before the house was sold. The first conversation is obligation free and we
            quote a fixed fee before drafting anything.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/granny-flat-consultation"
              className="inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Request a granny flat consultation
            </Link>
            <a
              href={FIRM.phoneHref}
              className="inline-flex items-center gap-2 rounded-sm border border-border px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
            >
              <Phone className="size-4 text-primary" aria-hidden="true" />
              Call {FIRM.phone}
            </a>
          </div>
        </div>

        <GuideList
          heading="Granny flat essentials"
          intro="The two core guides — how these arrangements work in Queensland, and how the structures compare."
          guides={pick(CORE_SLUGS)}
        />

        <div>
          <h2 className="font-serif text-2xl text-foreground">Free checklists</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Print these and work through them at the kitchen table before anyone commits.
          </p>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2">
            {DOWNLOADS.map((d) => (
              <li key={d.href} className="rounded-sm border border-border bg-card p-5">
                <a
                  href={d.href}
                  download
                  className="inline-flex items-center gap-2 font-serif text-lg text-primary underline"
                >
                  <Download className="size-4" aria-hidden="true" />
                  {d.label}
                </a>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d.note}</p>
              </li>
            ))}
          </ul>
        </div>

        <GuideList
          heading="Elder law angles"
          intro="Capacity, decision-making and the alternatives families weigh up alongside a granny flat — including retirement villages."
          guides={pick(ELDER_SLUGS)}
        />

        <GuideList
          heading="Estate planning and disputes"
          intro="A granny flat arrangement reshapes an estate. These guides cover updating wills, and what happens when other family members challenge the outcome."
          guides={pick(ESTATE_SLUGS)}
        />

        <GuideList
          heading="If a property is being bought or sold"
          intro="Most granny flat arrangements involve selling one home and buying or altering another. Our fixed-fee conveyancing covers both sides."
          guides={pick(PROPERTY_SLUGS)}
        />

        <div>
          <h2 className="font-serif text-2xl text-foreground">Services that go with it</h2>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { to: "/elder-law" as const, label: "Elder law", note: "Granny flat agreements, capacity, aged care and retirement villages." },
              { to: "/wills-and-estates" as const, label: "Wills & estates", note: "Wills, enduring powers of attorney, probate and estate administration." },
              { to: "/conveyancing" as const, label: "Fixed-fee conveyancing", note: "Selling the family home or buying the property everyone will share." },
              { to: "/fees" as const, label: "Our fees", note: "What we charge, in writing, before we start." },
            ].map((s) => (
              <li key={s.to}>
                <Link
                  to={s.to}
                  className="group flex h-full flex-col rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
                >
                  <span className="font-serif text-lg text-foreground group-hover:text-primary">
                    {s.label}
                  </span>
                  <span className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {s.note}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-3xl px-5 py-16">
          <h2 className="font-serif text-3xl text-foreground">Granny flat questions</h2>
          <dl className="mt-8 space-y-7">
            {FAQS.map((f) => (
              <div key={f.q}>
                <dt className="font-semibold text-foreground">{f.q}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-10 text-sm text-muted-foreground">
            We see clients at {FIRM_ADDRESS}, or at home and in aged care around Caloundra when
            getting out is difficult.
          </p>
          <div className="mt-6">
            <Link
              to="/granny-flat-consultation"
              className="inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Request a consultation
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
